import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
    EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export function extractEmotoradDealershipOuIdsFromUserValue(raw: unknown): string[] {
    if (raw === null || raw === undefined) {
        return [];
    }
    if (typeof raw === "string" && raw.trim() !== "") {
        return [raw.trim()];
    }
    if (Array.isArray(raw)) {
        return raw
            .map((item) =>
                typeof item === "string"
                    ? item
                    : (item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value,
            )
            .filter((item): item is string => typeof item === "string" && item.trim() !== "")
            .map((s) => s.trim());
    }
    if (typeof raw === "object") {
        const obj = raw as Record<string, unknown>;
        const candidate = obj.id ?? obj.value;
        if (typeof candidate === "string" && candidate.trim() !== "") {
            return [candidate.trim()];
        }
    }
    return [];
}

function userTemplateDisplayNameLower(templateId: Uuid, userTemplates: ReadonlyArray<UserTemplate>): string {
    const template = userTemplates.find((item) => item.id === templateId);
    if (template === undefined) {
        return "";
    }
    return template.displayName.toLowerCase().trim();
}

/**
 * Showroom id is the dealership organizational unit id. Resolve KAM / RM user ids by finding
 * workspace users linked to that dealership, then walking each user's `parentId` chain and
 * taking the nearest manager whose template reads as KAM or RM/ZSM.
 */
export function resolveKamRmUserIdsFromDealershipReportingChain(
    showroomOuId: Uuid,
    users: ReadonlyArray<User>,
    userTemplates: ReadonlyArray<UserTemplate>,
): {kamUserId: Uuid | null; rmUserId: Uuid | null} {
    const showroomKey = String(showroomOuId).toLowerCase();
    const dealershipUsers = users.filter((user) => {
        const linked = extractEmotoradDealershipOuIdsFromUserValue(
            user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID],
        );
        return linked.some((id) => id.toLowerCase() === showroomKey);
    });
    if (dealershipUsers.length === 0) {
        return {kamUserId: null, rmUserId: null};
    }

    const userByUserId = new Map<Uuid, User>();
    for (const user of users) {
        userByUserId.set(user.userId, user);
    }

    const isKamTemplate = (templateId: Uuid): boolean => {
        return userTemplateDisplayNameLower(templateId, userTemplates).includes("kam") === true;
    };
    const isRmTemplate = (templateId: Uuid): boolean => {
        const name = userTemplateDisplayNameLower(templateId, userTemplates);
        return name.includes("rm") === true || name.includes("zsm") === true;
    };

    let bestKam: {depth: number; id: Uuid} | null = null;
    let bestRm: {depth: number; id: Uuid} | null = null;

    for (const start of dealershipUsers) {
        const chainVisited = new Set<Uuid>();
        let depth = 0;
        let parentUserId: Uuid | null = start.parentId;
        while (parentUserId !== null) {
            depth += 1;
            if (chainVisited.has(parentUserId) === true) {
                break;
            }
            chainVisited.add(parentUserId);
            const manager = userByUserId.get(parentUserId);
            if (manager === undefined) {
                break;
            }
            if (isKamTemplate(manager.templateId) === true && (bestKam === null || depth < bestKam.depth)) {
                bestKam = {depth, id: manager.userId};
            }
            if (isRmTemplate(manager.templateId) === true && (bestRm === null || depth < bestRm.depth)) {
                bestRm = {depth, id: manager.userId};
            }
            parentUserId = manager.parentId;
        }
    }

    return {
        kamUserId: bestKam?.id ?? null,
        rmUserId: bestRm?.id ?? null,
    };
}

export type EmotoradDealerOuFieldKey = "contact" | "email" | "pincode";

export function getEmotoradDealerOuFieldValueByColumnLabels(
    dealerOuId: Uuid,
    field: EmotoradDealerOuFieldKey,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string | null {
    const dealerOu = organizationalUnits.find((unit) => unit.id === dealerOuId);
    if (dealerOu === undefined) {
        return null;
    }
    const template = organizationalUnitTemplates.find((item) => item.id === dealerOu.templateId);
    if (template === undefined) {
        return null;
    }
    const displayNamesByField: Record<EmotoradDealerOuFieldKey, ReadonlyArray<string>> = {
        contact: [
            "Contact Number",
            "Phone Number",
            "Mobile Number",
            "Contact",
            "Dealer Contact",
            "Dealer Contact Number",
        ],
        email: ["Email", "Email Id", "Email Address", "Dealer Email"],
        pincode: ["Pincode", "Pin Code", "Dealer Pincode"],
    };
    const displayNames = displayNamesByField[field];
    for (const displayName of displayNames) {
        const matchingColumn = template.additionalColumns.find((column) => {
            return column.displayName.trim().toLowerCase() === displayName.toLowerCase();
        });
        if (matchingColumn === undefined) {
            continue;
        }
        const rawValue = dealerOu.additionalColumnValues[matchingColumn.id];
        if (typeof rawValue === "string" && rawValue.trim() !== "") {
            return rawValue.trim();
        }
    }
    if (field === "contact") {
        const rawValue = dealerOu.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID];
        if (typeof rawValue === "string" && rawValue.trim() !== "") {
            return rawValue.trim();
        }
    }
    if (field === "email") {
        const rawValue = dealerOu.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID];
        if (typeof rawValue === "string" && rawValue.trim() !== "") {
            return rawValue.trim();
        }
    }
    return null;
}

function getEmotoradDealershipUserFieldValue(
    dealerOuId: Uuid,
    field: "contact" | "email" | "pincode",
    users: ReadonlyArray<User>,
): string | null {
    const fieldColumnId =
        field === "contact"
            ? EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID
            : field === "email"
              ? EMOTORAD__USER__EMAIL__COLUMN_ID
              : EMOTORAD__USER__ADDRESS_PINCODE__COLUMN_ID;
    const dealerKey = String(dealerOuId).toLowerCase();
    for (const user of users) {
        const linked = extractEmotoradDealershipOuIdsFromUserValue(
            user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID],
        );
        if (linked.some((id) => id.toLowerCase() === dealerKey) !== true) {
            continue;
        }
        const rawValue = user.additionalColumnValues[fieldColumnId];
        if (typeof rawValue === "string" && rawValue.trim() !== "") {
            return rawValue.trim();
        }
    }
    return null;
}

/** Resolves dealer contact/email/pincode for journey + event create payloads. */
export function resolveEmotoradDealerFieldForJourneySubmit(
    dealerOuId: Uuid,
    field: EmotoradDealerOuFieldKey,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    users: ReadonlyArray<User>,
): string | null {
    const fromOu = getEmotoradDealerOuFieldValueByColumnLabels(
        dealerOuId,
        field,
        organizationalUnits,
        organizationalUnitTemplates,
    );
    if (fromOu !== null) {
        return fromOu;
    }
    if (field === "contact" || field === "email" || field === "pincode") {
        return getEmotoradDealershipUserFieldValue(dealerOuId, field, users);
    }
    return null;
}
